import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Save, Briefcase, Building2, Loader2, Package, Info } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import {
  createWorkspace,
  updateWorkspace,
  listWorkspaceStatusTypes,
  type Workspace,
  type WorkspaceStatusType,
} from "@/services/workspacesService";
import { listClients, type Client } from "@/services/clientsService";
import { listProducts, type Product } from "@/services/productsService";

interface WorkspaceFormProps {
  onBack: () => void;
  onSave: () => void;
  editingWorkspace: Workspace | null;
}

interface FormData {
  identifier: string;
  name: string;
  description: string;
  client_id: string;
  product_ids: string[];
  status_id: number;
}

export const WorkspaceForm = ({
  onBack,
  onSave,
  editingWorkspace,
}: WorkspaceFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    identifier: "",
    name: "",
    description: "",
    client_id: "",
    product_ids: [],
    status_id: 1,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [statusTypes, setStatusTypes] = useState<WorkspaceStatusType[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadFormData();
  }, [editingWorkspace]);

  const loadFormData = async () => {
    setLoading(true);
    try {
      const [clientsRes, productsRes, statusTypesRes] = await Promise.all([
        listClients({ status_id: 1 }),
        listProducts(),
        listWorkspaceStatusTypes(),
      ]);

      if (clientsRes.success && clientsRes.data) {
        const clientsArray = (clientsRes.data as any).clients || [];
        setClients(Array.isArray(clientsArray) ? clientsArray : []);
      }

      if (productsRes.success && productsRes.data) {
        const productsArray = (productsRes.data as any).products || [];
        setProducts(Array.isArray(productsArray) ? productsArray : []);
      }

      if (statusTypesRes.success && statusTypesRes.data) {
        setStatusTypes(Array.isArray(statusTypesRes.data) ? statusTypesRes.data : []);
      }

      if (editingWorkspace) {
        const workspace = editingWorkspace;
        setFormData({
          identifier: workspace.identifier || "",
          name: workspace.name || "",
          description: workspace.description || "",
          client_id: workspace.client_id || "",
          product_ids: workspace.products ? workspace.products.map(p => p.id) : [],
          status_id: workspace.status_id || 1,
        });
        setSelectedClientId(workspace.client_id || "");
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getAvailableProducts = (clientId: string) => {
    if (!clients || !products || !clientId) return [];

    const client = clients.find(c => c.id === clientId);
    if (!client || !client.products) return [];

    return products.filter(product =>
      client.products.some(cp => cp.id === product.id)
    );
  };

  const handleSubmit = async () => {
    if (!formData.identifier || formData.identifier.trim().length === 0) {
      toast({
        title: "Erro de validação",
        description: "Identificador é obrigatório",
        variant: "destructive"
      });
      return;
    }

    const identifierRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!identifierRegex.test(formData.identifier)) {
      toast({
        title: "Erro de validação",
        description: "Identificador deve conter apenas letras minúsculas, números e hífens, sem espaços",
        variant: "destructive"
      });
      return;
    }

    if (!formData.name || formData.name.trim().length < 2) {
      toast({
        title: "Erro de validação",
        description: "Nome do workspace deve ter pelo menos 2 caracteres",
        variant: "destructive"
      });
      return;
    }

    if (!formData.client_id) {
      toast({
        title: "Erro de validação",
        description: "Selecione um cliente",
        variant: "destructive"
      });
      return;
    }

    if (formData.product_ids.length === 0) {
      toast({
        title: "Erro de validação",
        description: "Selecione pelo menos um especialista",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);

    try {
      let result;

      if (editingWorkspace) {
        result = await updateWorkspace(editingWorkspace.id, formData);

        if (result.success) {
          toast({
            title: "Workspace atualizado",
            description: `Workspace "${formData.name}" foi atualizado com sucesso.`
          });
          onSave();
        }
      } else {
        result = await createWorkspace(formData);

        if (result.success) {
          toast({
            title: "Workspace criado",
            description: `Workspace "${formData.name}" foi criado com sucesso.`
          });
          onSave();
        }
      }

      if (!result.success) {
        toast({
          title: "Erro",
          description: result.error?.message || "Erro ao salvar workspace",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar workspace",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProductSelection = (productId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      product_ids: checked
        ? [...prev.product_ids, productId]
        : prev.product_ids.filter(id => id !== productId)
    }));
  };

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      product_ids: []
    }));
  };

  const availableProducts = getAvailableProducts(selectedClientId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Briefcase}
        title={editingWorkspace ? "Editar Workspace" : "Novo Workspace"}
        description={
          editingWorkspace
            ? "Atualize as informações do workspace"
            : "Configure um novo workspace no sistema"
        }
        onBack={onBack}
        backDisabled={saving}
        actions={
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-gradient-primary text-white hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar Workspace
              </>
            )}
          </Button>
        }
      />

      <main className="container mx-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure os dados principais do workspace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="client">Cliente *</Label>
                {clients && clients.length > 0 ? (
                  <Select value={formData.client_id} onValueChange={handleClientChange} disabled={saving}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          <div className="flex items-center space-x-2">
                            <Building2 className="h-4 w-4" />
                            <span>{client.name} - {client.company}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex items-center justify-center h-10 px-3 py-2 text-sm border rounded-md bg-muted text-muted-foreground">
                    Nenhum cliente disponível. Cadastre clientes primeiro.
                  </div>
                )}
              </div>

              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="identifier">Identificador *</Label>
                  <div className="group relative">
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    <div className="absolute left-0 top-6 z-50 hidden group-hover:block w-72 p-3 bg-popover text-popover-foreground text-sm rounded-md border shadow-md">
                      <p className="font-medium mb-1">Regras do Identificador:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Apenas letras minúsculas (a-z)</li>
                        <li>Números (0-9)</li>
                        <li>Hífens (-) para separação</li>
                        <li>Sem espaços ou caracteres especiais</li>
                        <li>Deve ser único dentro do cliente selecionado</li>
                        <li>Exemplo: atendimento-cliente ou marketing-digital</li>
                      </ul>
                      <p className="text-xs mt-2 text-muted-foreground">
                        <strong>Nota:</strong> Clientes diferentes podem ter workspaces com o mesmo identificador
                      </p>
                    </div>
                  </div>
                </div>
                <Input
                  id="identifier"
                  value={formData.identifier}
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, identifier: e.target.value.toLowerCase() }))
                  }
                  placeholder="Ex: atendimento-cliente"
                  disabled={saving || !formData.client_id}
                />
                {!formData.client_id && (
                  <p className="text-xs text-muted-foreground">
                    Selecione um cliente primeiro
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome do Workspace *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Atendimento ao Cliente, Marketing Digital..."
                    disabled={saving}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status_id.toString()}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status_id: parseInt(value) }))}
                    disabled={saving}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusTypes && statusTypes.length > 0 ? (
                        statusTypes.map((status) => (
                          <SelectItem key={status.id} value={status.id.toString()}>
                            {status.name === 'active' ? 'Ativo' : 'Inativo'}
                          </SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="1">Ativo</SelectItem>
                          <SelectItem value="2">Inativo</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o propósito e objetivos deste workspace..."
                  rows={4}
                  disabled={saving}
                />
              </div>
            </CardContent>
          </Card>

          {/* Products */}
          {selectedClientId && (
            <Card>
              <CardHeader>
                <CardTitle>Especialistas Disponíveis</CardTitle>
                <CardDescription>
                  Selecione os especialistas que estarão disponíveis neste workspace
                </CardDescription>
              </CardHeader>
              <CardContent>
                {availableProducts.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {availableProducts.map((product) => (
                        <div
                          key={product.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            formData.product_ids.includes(product.id)
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                          onClick={() => handleProductSelection(product.id, !formData.product_ids.includes(product.id))}
                        >
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              id={`product-${product.id}`}
                              checked={formData.product_ids.includes(product.id)}
                              onCheckedChange={(checked) =>
                                handleProductSelection(product.id, checked as boolean)
                              }
                              onClick={(e) => e.stopPropagation()}
                              disabled={saving}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <Package className="h-4 w-4 text-muted-foreground" />
                                <Label
                                  htmlFor={`product-${product.id}`}
                                  className="text-sm font-medium cursor-pointer"
                                >
                                  {product.name}
                                </Label>
                              </div>
                              {product.description && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {product.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.product_ids.length > 0 && (
                      <div className="mt-4 flex items-center space-x-2">
                        <Badge variant="secondary">
                          {formData.product_ids.length} especialista(s) selecionado(s)
                        </Badge>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      Este cliente não possui especialistas contratados
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

